import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAllPublicTenantProfiles } from "../api/tenantProfileApi";

const PublicTenantDirectory = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const loadPublicDirectory = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAllPublicTenantProfiles();
      const list = res?.data || res || [];
      if (Array.isArray(list)) {
        setProfiles(list);
      } else {
        setProfiles([]);
      }
    } catch (err) {
      console.error("Failed to load public tenant directory:", err);
      setError(err?.message || "Failed to load tenant profiles directory.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPublicDirectory();
  }, []);

  const filteredProfiles = profiles.filter((p) => {
    const heading = (p.heading || "").toLowerCase();
    const desc = (p.description || "").toLowerCase();
    const query = searchQuery.toLowerCase().trim();
    return heading.includes(query) || desc.includes(query);
  });

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Public Business Profiles
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
            Explore verified organizations and business partners on DocLift.
          </p>

          {/* Search bar */}
          <div className="mt-6 max-w-md mx-auto relative">
            <input
              type="text"
              placeholder="Search companies by name or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-2xl border border-slate-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#096dea] focus:border-transparent text-sm bg-white"
            />
            <svg
              className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[#096dea] border-t-transparent"></div>
              <p className="mt-3 text-sm font-medium text-slate-600">Loading directory...</p>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-md mx-auto text-center rounded-3xl bg-white p-8 shadow-sm border border-slate-200 py-12">
            <p className="text-rose-600 text-sm font-medium">{error}</p>
            <button
              onClick={loadPublicDirectory}
              className="mt-4 px-4 py-2 bg-[#096dea] text-white text-xs font-semibold rounded-xl"
            >
              Retry
            </button>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 shadow-sm max-w-lg mx-auto">
            <p className="text-slate-500 text-sm">
              {searchQuery ? `No profiles found for "${searchQuery}"` : "No public tenant profiles available."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProfiles.map((tenant) => {
              const tenantId = tenant.bot_id || tenant.tenant_id || tenant.id;
              const logoUrl = tenant.logo_data
                ? `data:${tenant.logo_mime_type || "image/png"};base64,${tenant.logo_data}`
                : null;

              return (
                <Link
                  key={tenantId}
                  to={tenant.slug ? `/public/${encodeURIComponent(tenant.slug)}` : `/public/tenants/${tenantId}`}
                  className="group bg-white rounded-3xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:border-[#096dea]/30 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={tenant.heading || "Company Logo"}
                          className="w-14 h-14 rounded-2xl object-contain border border-slate-200 bg-white p-1.5 shadow-sm group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-500 text-xl shadow-sm">
                          {(tenant.heading || "T").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h2 className="font-bold text-slate-900 text-lg group-hover:text-[#096dea] transition-colors line-clamp-1">
                          {tenant.heading || "Business Profile"}
                        </h2>
                        <span className="inline-block mt-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                          Verified Public Profile
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4 min-h-[3.5rem]">
                      {tenant.description || "No description provided."}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-[#096dea]">
                    <span>View Full Profile</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicTenantDirectory;
